/**
 * 通知帧的**域值**（帧名 → i18n 键）。
 *
 * 这张表是**协议事实**的前端侧镜像：帧名是后端帧集（真源
 * `backend/frames/catalog.yaml`，生成物 `backend/src/frames/definitions.py`）
 * 里那些类的值。它与「怎么显示」无关，所以住在域层——通知中心与
 * 事件流两个 feature 都读它（此前住在 `features/notifications/model.ts`，
 * 于是 `features/events` 为了读一个键反过来 import 另一个 feature，成了一对
 * 循环依赖）。
 *
 * ⚠️ 帧的**配色**不在这儿——那是 UI 决策，住 feature 层（通知中心自己那屏）。
 */
export const NOTIFICATION_FRAMES = [
  'account.online',
  'account.need_code',
  'account.failed',
  'redeem.card_processed',
  'redeem.attribute',
  'redeem.expired',
  'cdkey.bound',
  'order.account.granted',
  'order.account.revoked',
  'order.account.deleted',
  'fx.refresh_failed',
  'proxy.check_speed_done',
  'notification.broadcast',
] as const;
export type NotificationFrame = (typeof NOTIFICATION_FRAMES)[number];
/**
 * 帧名 → i18n 键。
 *
 * ⚠️ **帧名改了，i18n 键不改** —— i18n 键是**文案标识**，改键等于改文案
 * 契约（后端 catalog 里 `cdkey.bound` 的 `i18n` 仍写
 * `notifications.frame.cdkey_redeemed`，`order.account.granted` 仍写
 * `notifications.frame.grant_granted`）⇒ 两边照同一份目录走。
 */
const FRAME_KEY: Record<NotificationFrame, string> = {
  'account.online': 'notifications.frame.account_online',
  'account.need_code': 'notifications.frame.account_need_code',
  'account.failed': 'notifications.frame.account_failed',
  'redeem.card_processed': 'notifications.frame.redeem_card_processed',
  'redeem.attribute': 'notifications.frame.redeem_attribute',
  'redeem.expired': 'notifications.frame.redeem_expired',
  'cdkey.bound': 'notifications.frame.cdkey_redeemed',
  'order.account.granted': 'notifications.frame.grant_granted',
  'order.account.revoked': 'notifications.frame.grant_revoked',
  'order.account.deleted': 'notifications.frame.order_account_deleted',
  'fx.refresh_failed': 'notifications.frame.fx_refresh_failed',
  'proxy.check_speed_done': 'notifications.frame.proxy_check_speed_done',
  'notification.broadcast': 'notifications.frame.broadcast',
};
export const FALLBACK_FRAME_KEY = 'notifications.frame.unknown';
export function frameKey(frame: string): string {
  return FRAME_KEY[frame as NotificationFrame] ?? FALLBACK_FRAME_KEY;
}

/**
 * **打断集**——哪几帧会用浮层（toast）打断人。
 *
 * 这张表是 `backend/frames/catalog.yaml` 的**第二张前端侧镜像**（第一张是上面
 * 的 i18n 键表）⇒ 两张表同源：目录改一帧的 `notice` 档，这里就要跟着改。
 * 跨端闸 `frontend/src/features/events/frameCatalogParity.test.ts` 逐条对账
 * （它直接读后端 `catalog.yaml` ⇒ 少了 ⇒ 该弹的不弹；多了 ⇒ 不该弹的弹了）。
 *
 * ## 打断有**两道**判据，缺一不可
 *
 * | # | 判据 | 谁算 | 管什么 |
 * |---|---|---|---|
 * | ① | 行的 `importance` 是 `IMPORTANT` | **后端**（收件人 ∈ waiter ∪ anchor） | 该不该打扰**这个人**（admin 不该被别人的登录失败打扰） |
 * | ② | 来源帧在**打断集**里 | **本表**（= 目录 `notice: act` 的那几帧） | 这一类事实该不该用**浮层**打断 |
 *
 * ⇒ 两道都过才弹。**只过 ① 不过 ②** 的典型是兑换失败：它在铃铛里是**重要行**
 * （角标 +1、列表里带「重要」标）⇒ **看得见**，但**不用浮层打断**
 * （用户裁定 2026-10-23：`redeem.card_processed` 不弹）。
 */
export const INTERRUPTING_FRAMES = [
  'account.need_code',
  'account.failed',
  'cdkey.bound',
  'fx.refresh_failed',
] as const;
export type InterruptingFrame = (typeof INTERRUPTING_FRAMES)[number];
const INTERRUPTING_SET: ReadonlySet<string> = new Set(INTERRUPTING_FRAMES);
/** 这一帧值不值得用浮层打断（= 在打断集里）。 */
export function isInterruptingFrame(frame: string): boolean {
  return INTERRUPTING_SET.has(frame);
}
